import { useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError, BrowserAuthError } from '@azure/msal-browser';
import { createFlowClient, FlowClient } from '../utils/flowClient';
import { getDemoSessionToken } from '../utils/demoSession';

const TOKEN_SCOPES = ['openid', 'profile', 'email'];

export function useFlowClient(): FlowClient {
  const { instance } = useMsal();

  const clientRef = useRef<FlowClient | null>(null);
  if (!clientRef.current) {
    // Demo mode — a server-issued session token stands in for MSAL entirely. No account,
    // no token acquisition, no popup; every request carries X-Demo-Session instead.
    const demoToken = getDemoSessionToken();
    if (demoToken) {
      clientRef.current = createFlowClient(async () => '', demoToken);
      return clientRef.current;
    }

    const getToken = async (): Promise<string> => {
      const accounts = instance.getAllAccounts();
      if (accounts.length === 0) throw new Error('No authenticated account');

      try {
        const result = await instance.acquireTokenSilent({
          scopes: TOKEN_SCOPES,
          account: accounts[0],
        });
        return result.idToken;
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError || err instanceof BrowserAuthError) {
          const result = await instance.acquireTokenPopup({
            scopes: TOKEN_SCOPES,
            account: accounts[0],
          });
          return result.idToken;
        }
        throw err;
      }
    };
    clientRef.current = createFlowClient(getToken);
  }

  return clientRef.current;
}
